import React, { useState, useEffect, useRef } from 'react';
import { AnalyticsDashboard } from './AnalyticsDashboardNew';
import ServerHealthView from './ServerHealthView';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://ahobilam.com';
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

// Main Admin Panel Component
const AdminPanel = ({ user, language }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [editPostSlug, setEditPostSlug] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (activeView === 'dashboard') {
      fetchStats();
    }
  }, [activeView]);

  // Handle edit post from URL parameter
  useEffect(() => {
    const editPost = searchParams.get('editPost');
    if (editPost) {
      setEditPostSlug(editPost);
      setActiveView('editpost');
    }
  }, [searchParams]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const statsRes = await axios.get(`${API}/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      setStats({
        total_posts: 0,
        total_users: 0,
        pending_comments: 0,
        total_comments: 0
      });
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 font-sans">
      {/* Sidebar Navigation */}
      <div className="w-64 bg-gray-900 text-white flex flex-col shadow-2xl relative z-20">
        <div className="p-6 bg-gray-800 border-b border-gray-700/50">
          <h1 className="text-2xl font-bold tracking-tight text-white flex items-center">
             <span className="mr-2 opacity-80">🕉️</span> Admin
          </h1>
          <p className="text-xs text-gray-400 mt-2 font-medium flex items-center">
             <span className="w-2 h-2 rounded-full bg-green-500 mr-2"></span>
             Welcome, {user?.name?.split(' ')[0]}
          </p>
        </div>
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {[
            { id: 'dashboard', icon: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z', label: 'Dashboard Overview' },
            { id: 'posts', icon: 'M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z', label: 'Manage Posts' },
            { id: 'newpost', icon: 'M12 4v16m8-8H4', label: 'Create New Post' },
            { id: 'users', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z', label: 'User Directory' },
            { id: 'qa', icon: 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z', label: 'Q&A Board' },
            { id: 'shastra', icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253', label: '📜 Shastra Rules (6,500+)' },
            { id: 'divyadesams', icon: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4', label: '🛕 108 Divyadesams CMS' },
            { id: 'analytics', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', label: '📊 Analytics' },
            { id: 'server-health', icon: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z', label: '🖥️ Server Health' }
          ].map(item => (
            <button
              key={item.id}
              onClick={() => setActiveView(item.id)}
              className={`flex items-center w-full px-4 py-3 rounded-xl transition-all duration-200 ${
                activeView === item.id || (item.id === 'posts' && activeView === 'editpost')
                  ? 'bg-blue-600 text-white shadow-md shadow-blue-900/20'
                  : 'text-gray-400 hover:bg-gray-800 hover:text-white'
              }`}
            >
              <svg className="w-5 h-5 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
              </svg>
              <span className="font-medium text-sm">{item.label}</span>
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-gray-800">
           <button 
             onClick={() => window.location.href = '/'}
             className="flex items-center w-full px-4 py-3 text-sm font-medium text-gray-400 bg-gray-800/50 hover:bg-gray-800 hover:text-white rounded-xl transition-colors"
           >
             <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
             Back to Website
           </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden bg-gray-50 relative z-10">
        <header className="bg-white/80 backdrop-blur-md shadow-sm border-b border-gray-200/60 px-8 py-5 flex justify-between items-center sticky top-0 z-20">
          <h2 className="text-2xl font-bold text-gray-800 capitalize tracking-tight flex items-center">
            {activeView === 'newpost' ? 'Create New Post' : 
             activeView === 'editpost' ? 'Edit Post' : 
             activeView.replace('-', ' ')}
          </h2>
        </header>

        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-50 p-8">
          <div className="max-w-6xl mx-auto pb-12">
            {activeView === 'dashboard' && <DashboardView stats={stats} setActiveView={setActiveView} />}
            {activeView === 'posts' && <PostsListView setActiveView={setActiveView} />}
            {activeView === 'newpost' && <PostEditorView setActiveView={setActiveView} />}
            {activeView === 'editpost' && editPostSlug && <PostEditorView postSlug={editPostSlug} setActiveView={setActiveView} />}
            {activeView === 'users' && <UsersListView user={user} />}
            {activeView === 'qa' && <QAAdminView />}
            {activeView === 'shastra' && <ShastraRulesView />}
            {activeView === 'divyadesams' && <DivyadesamsAdminView />}
            {activeView === 'analytics' && <AnalyticsDashboard />}
            {activeView === 'server-health' && <ServerHealthView />}
          </div>
        </main>
      </div>
    </div>
  );
};

// Dashboard View - Modern Dashboard UI
const DashboardView = ({ stats, setActiveView }) => {
  const [qaTotal, setQaTotal] = useState(0);

  useEffect(() => {
    const fetchQaTotal = async () => {
      try {
        const res = await axios.get(`${API}/qa/list?limit=1`);
        if (res.data && res.data.total) {
          setQaTotal(res.data.total);
        }
      } catch (err) {
        console.error('Failed to fetch QA stats');
      }
    };
    fetchQaTotal();
  }, []);

  if (!stats) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Overview Metric Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <StatCard title="Total Blog Posts" value={stats.total_posts} icon="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" color="blue" onClick={() => setActiveView('posts')} />
        <StatCard title="Registered Users" value={stats.total_users} icon="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" color="emerald" onClick={() => setActiveView('users')} />
        <StatCard title="Q&A Threads" value={qaTotal || '...'} icon="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" color="cyan" onClick={() => setActiveView('qa')} />
        <StatCard title="Pending Comments" value={stats.pending_comments} icon="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" color="amber" onClick={() => window.location.href = '/admin/comments'} />
        <StatCard title="Total Comments" value={stats.total_comments} icon="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" color="purple" onClick={() => window.location.href = '/admin/comments'} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Real-time Visitor Analysis */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100 bg-white flex justify-between items-center">
            <h3 className="text-lg font-bold text-gray-800 tracking-tight flex items-center">
              <span className="text-blue-500 mr-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
              </span>
              Visitor Analysis
            </h3>
            <span className="flex h-3 w-3 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
            </span>
          </div>
          <div className="p-6 grid grid-cols-2 gap-6 bg-gray-50/50">
            <div className="p-5 bg-white rounded-xl border border-gray-100 text-center shadow-sm hover:shadow-md transition-shadow">
              <p className="text-sm font-medium text-gray-500 mb-2 uppercase tracking-wide">Public Users Now</p>
              <p className="text-5xl font-black text-blue-600 tracking-tighter">{stats.traffic_stats?.active_users || 0}</p>
              <p className="text-xs text-gray-400 mt-2">In the last 15 minutes</p>
              <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between px-2 text-xs text-gray-500">
                <span>Admin:</span>
                <span className="font-bold">{stats.traffic_stats?.admin_active || 0}</span>
              </div>
            </div>
            <div className="p-5 bg-white rounded-xl border border-gray-100 text-center shadow-sm hover:shadow-md transition-shadow">
              <p className="text-sm font-medium text-gray-500 mb-2 uppercase tracking-wide">Public Visits Today</p>
              <p className="text-5xl font-black text-indigo-600 tracking-tighter">{stats.traffic_stats?.today_hits || 0}</p>
              <p className="text-xs text-gray-400 mt-2">Total page views</p>
              <div className="mt-3 pt-3 border-t border-gray-100 flex justify-between px-2 text-xs text-gray-500">
                <span>Admin:</span>
                <span className="font-bold">{stats.traffic_stats?.admin_hits || 0}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Feature Usage Stats */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-100 bg-white">
            <h3 className="text-lg font-bold text-gray-800 tracking-tight flex items-center">
              <span className="text-purple-500 mr-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
              </span>
              Feature Usage Metrics
            </h3>
          </div>
          <div className="p-6 grid grid-cols-2 gap-6 bg-gray-50/50">
            <div className="p-5 bg-gradient-to-br from-blue-50 to-white rounded-xl border border-blue-100 text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
              </div>
              <p className="text-sm font-medium text-gray-600 mb-1">PDFs Generated</p>
              <p className="text-3xl font-bold text-gray-900">{stats.usage_stats?.total_pdf_generations || 0}</p>
            </div>
            <div className="p-5 bg-gradient-to-br from-purple-50 to-white rounded-xl border border-purple-100 text-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>
              </div>
              <p className="text-sm font-medium text-gray-600 mb-1">Premium Views</p>
              <p className="text-3xl font-bold text-gray-900">{stats.usage_stats?.total_premium_views || 0}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Generic reusable stat card component
const StatCard = ({ title, value, icon, color, onClick }) => {
  const colorMap = {
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    amber: 'bg-amber-50 text-amber-600 border-amber-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
    cyan: 'bg-cyan-50 text-cyan-600 border-cyan-100',
  };

  const textMap = {
    blue: 'text-blue-600',
    emerald: 'text-emerald-600',
    amber: 'text-amber-600',
    purple: 'text-purple-600',
    cyan: 'text-cyan-600',
  };

  return (
    <div 
      onClick={onClick}
      className={`p-6 rounded-2xl border cursor-pointer transition-all duration-300 transform hover:-translate-y-1 hover:shadow-xl bg-white flex flex-col justify-between ${colorMap[color]}`}
    >
      <div className="flex justify-between items-start mb-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-white shadow-sm ${textMap[color]}`}>
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={icon} /></svg>
        </div>
      </div>
      <div>
        <p className="text-3xl font-black text-gray-900 tracking-tight">{value}</p>
        <p className="text-sm font-medium text-gray-600 mt-1">{title}</p>
      </div>
    </div>
  );
};

// Posts List View
const PostsListView = ({ setActiveView }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const postsPerPage = 10;

  useEffect(() => {
    fetchPosts();
  }, [currentPage]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/blog/posts?limit=${postsPerPage}&page=${currentPage}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPosts(response.data);
      // Get total count from response or use posts length
      setTotalPosts(response.data.total || response.data.length);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const deletePost = async (slug) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/blog/posts/${slug}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert('Post deleted successfully!');
      fetchPosts();
    } catch (error) {
      alert('Error deleting post');
    }
  };

  if (editingPost) {
    return <PostEditorView post={editingPost} setActiveView={setActiveView} onBack={() => setEditingPost(null)} />;
  }

  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      <div className="p-6 border-b border-gray-100 bg-white flex justify-between items-center">
        <div>
          <h3 className="text-xl font-bold text-gray-800 tracking-tight">Blog Posts</h3>
          <p className="text-sm text-gray-500 mt-1">{totalPosts} posts published</p>
        </div>
        <button
          onClick={() => setActiveView('newpost')}
          className="bg-blue-600 text-white px-5 py-2.5 rounded-xl hover:bg-blue-700 font-medium transition-colors shadow-sm shadow-blue-200 flex items-center"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          New Post
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50/50 border-b border-gray-100">
            <tr>
              <th className="text-left py-4 px-6 font-semibold text-gray-600 text-sm tracking-wide">Title</th>
              <th className="text-left py-4 px-6 font-semibold text-gray-600 text-sm tracking-wide">Category</th>
              <th className="text-left py-4 px-6 font-semibold text-gray-600 text-sm tracking-wide">Published</th>
              <th className="text-left py-4 px-6 font-semibold text-gray-600 text-sm tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {posts.map(post => (
              <tr key={post.slug} className="hover:bg-gray-50 transition-colors">
                <td className="py-4 px-6">
                  <div className="font-semibold text-gray-800">{post.title_en || post.title}</div>
                  {post.title_ta && post.title_ta !== post.title_en && (
                    <div className="text-xs text-indigo-600 mt-1 font-medium">{post.title_ta}</div>
                  )}
                </td>
                <td className="py-4 px-6">
                  <span className="bg-blue-50 text-blue-700 border border-blue-100 text-xs px-3 py-1 rounded-full font-medium">
                    {post.category || 'General'}
                  </span>
                </td>
                <td className="py-4 px-6 text-sm text-gray-600 font-medium">{new Date(post.published_at).toLocaleDateString()}</td>
                <td className="py-4 px-6">
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingPost(post)}
                      className="text-indigo-600 bg-indigo-50 hover:bg-indigo-100 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deletePost(post.slug)}
                      className="text-red-600 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="p-4 border-t border-gray-100 bg-gray-50/50 flex justify-between items-center">
        <div className="text-sm font-medium text-gray-500">
          Showing page {currentPage}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium text-gray-700 transition-colors"
          >
            Previous
          </button>
          <button
            onClick={() => setCurrentPage(p => p + 1)}
            disabled={posts.length < postsPerPage}
            className="px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium text-gray-700 transition-colors"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

// Post Editor View with Quill
const PostEditorView = ({ post = null, postSlug = null, setActiveView, onBack }) => {
  const [loadedPost, setLoadedPost] = useState(post);
  const [loadingPost, setLoadingPost] = useState(!!postSlug);
  const [formData, setFormData] = useState({
    title_en: post?.title_en || post?.title || '',
    title_ta: post?.title_ta || '',
    slug: post?.slug || '',
    content_en: post?.content_en || post?.content || '',
    content_ta: post?.content_ta || '',
    category: post?.category || 'General',
    tags: post?.tags?.join(', ') || '',
    featured_image: post?.featured_image || ''
  });
  const [loading, setLoading] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [editorReady, setEditorReady] = useState(false);

  const quillEnRef = useRef(null);
  const quillTaRef = useRef(null);
  const editorEnRef = useRef(null);
  const editorTaRef = useRef(null);

  // Load post by slug if provided
  useEffect(() => {
    if (postSlug && !post) {
      loadPost(postSlug);
    }
  }, [postSlug]);

  const loadPost = async (slug) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/blog/post/${slug}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const postData = response.data;
      setLoadedPost(postData);
      setFormData({
        title_en: postData.title_en || postData.title || '',
        title_ta: postData.title_ta || '',
        slug: postData.slug || '',
        content_en: postData.content_en || postData.content || '',
        content_ta: postData.content_ta || '',
        category: postData.category || 'General',
        tags: postData.tags?.join(', ') || '',
        featured_image: postData.featured_image || ''
      });
    } catch (error) {
      console.error('Error loading post:', error);
      alert('Error loading post');
    } finally {
      setLoadingPost(false);
    }
  };

  useEffect(() => {
    if (window.Quill && !quillEnRef.current && editorEnRef.current && editorTaRef.current) {
      setTimeout(() => {
        const toolbarOptions = [
          [{ 'header': [1, 2, 3, false] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ 'list': 'ordered'}, { 'list': 'bullet' }],
          [{ 'color': [] }, { 'background': [] }],
          [{ 'align': [] }],
          ['link', 'image'],
          ['blockquote', 'code-block'],
          [{ 'script': 'sub'}, { 'script': 'super' }],
          ['clean']
        ];

        quillEnRef.current = new window.Quill(editorEnRef.current, {
          theme: 'snow',
          modules: { toolbar: toolbarOptions },
          placeholder: 'Write content in English...'
        });

        quillTaRef.current = new window.Quill(editorTaRef.current, {
          theme: 'snow',
          modules: { toolbar: toolbarOptions },
          placeholder: 'Write content in Tamil...'
        });

        if (formData.content_en) {
          quillEnRef.current.root.innerHTML = formData.content_en;
        }
        if (formData.content_ta) {
          quillTaRef.current.root.innerHTML = formData.content_ta;
        }

        quillEnRef.current.on('text-change', () => setHasChanges(true));
        quillTaRef.current.on('text-change', () => setHasChanges(true));
        
        setEditorReady(true);
      }, 100);
    }
  }, [editorEnRef.current, editorTaRef.current]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const content_en = quillEnRef.current ? quillEnRef.current.root.innerHTML : formData.content_en;
    const content_ta = quillTaRef.current ? quillTaRef.current.root.innerHTML : formData.content_ta;

    const submitData = {
      ...formData,
      content_en,
      content_ta,
      tags: formData.tags.split(',').map(t => t.trim()).filter(t => t),
      author: JSON.parse(localStorage.getItem('user'))?.name || 'Admin',
      published_at: new Date().toISOString()
    };

    try {
      const token = localStorage.getItem('token');
      const url = post ? `${API}/blog/posts/${post.slug}` : `${API}/blog/posts`;
      const method = post ? 'put' : 'post';
      
      await axios[method](url, submitData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      alert(post ? 'Post updated successfully!' : 'Post created successfully!');
      setHasChanges(false);
      if (onBack) {
        onBack();
      } else {
        setActiveView('posts');
      }
    } catch (error) {
      alert('Error saving post: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges && !window.confirm('Discard unsaved changes?')) return;
    if (onBack) {
      onBack();
    } else {
      setActiveView('posts');
    }
  };

  if (loadingPost) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 text-center">
        <div className="text-xl">Loading post...</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold">{(post || loadedPost) ? '✏️ Edit Post' : '➕ Create New Post'}</h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="flex justify-between items-center mb-4">
          <button
            type="button"
            onClick={() => { onBack ? onBack() : setActiveView('posts'); }}
            className="text-blue-600 hover:text-blue-800 flex items-center gap-2"
          >
            ← Back to Posts
          </button>
          
          {/* Quick Style Buttons */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                const range = quillEnRef.current?.getSelection();
                if (range) {
                  const currentContent = quillEnRef.current.root.innerHTML;
                  const table = `<table border="1" style="width:100%; border-collapse: collapse;"><tr><th>Header 1</th><th>Header 2</th></tr><tr><td>Cell 1</td><td>Cell 2</td></tr></table>`;
                  quillEnRef.current.clipboard.dangerouslyPasteHTML(range.index, table);
                }
              }}
              className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
              title="Insert Table"
            >
              📊 Table
            </button>
            
            <select
              onChange={(e) => {
                const className = e.target.value;
                if (className && quillEnRef.current) {
                  const range = quillEnRef.current.getSelection();
                  if (range) {
                    const text = quillEnRef.current.getText(range.index, range.length);
                    const styledDiv = `<div class="${className}">${text || 'Styled content here'}</div>`;
                    quillEnRef.current.deleteText(range.index, range.length);
                    quillEnRef.current.clipboard.dangerouslyPasteHTML(range.index, styledDiv);
                  }
                }
                e.target.value = '';
              }}
              className="px-2 py-1 border rounded text-sm"
            >
              <option value="">+ Style Class</option>
              <option value="svk">Yellow Highlight (svk)</option>
              <option value="head2">Blue Header (head2)</option>
              <option value="head3">Red Header (head3)</option>
              <option value="greenhd">Green Header (greenhd)</option>
              <option value="div2">Border Box (div2)</option>
              <option value="div3">Violet Border (div3)</option>
              <option value="mydiv">Red Border Box (mydiv)</option>
              <option value="hint2">Dark Hint (hint2)</option>
              <option value="color4">Yellow BG (color4)</option>
              <option value="sans">Sans Serif Text (sans)</option>
            </select>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium mb-2">Title (English) *</label>
            <input
              type="text"
              value={formData.title_en}
              onChange={(e) => {
                setFormData({...formData, title_en: e.target.value});
                setHasChanges(true);
                if (!post && !formData.slug) {
                  setFormData({
                    ...formData,
                    title_en: e.target.value,
                    slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
                  });
                }
              }}
              className="w-full border rounded px-4 py-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Title (Tamil)</label>
            <input
              type="text"
              value={formData.title_ta}
              onChange={(e) => {
                setFormData({...formData, title_ta: e.target.value});
                setHasChanges(true);
              }}
              className="w-full border rounded px-4 py-2"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Slug *</label>
          <input
            type="text"
            value={formData.slug}
            onChange={(e) => {
              setFormData({...formData, slug: e.target.value});
              setHasChanges(true);
            }}
            className="w-full border rounded px-4 py-2"
            readOnly={!!post}
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Content (English) *</label>
          <div ref={editorEnRef} style={{ height: '300px' }} className="bg-white border rounded" />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Content (Tamil)</label>
          <div ref={editorTaRef} style={{ height: '300px' }} className="bg-white border rounded" />
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium mb-2">Category</label>
            <input
              type="text"
              value={formData.category}
              onChange={(e) => {
                setFormData({...formData, category: e.target.value});
                setHasChanges(true);
              }}
              className="w-full border rounded px-4 py-2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Tags (comma-separated)</label>
            <input
              type="text"
              value={formData.tags}
              onChange={(e) => {
                setFormData({...formData, tags: e.target.value});
                setHasChanges(true);
              }}
              className="w-full border rounded px-4 py-2"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Featured Image URL</label>
          <input
            type="url"
            value={formData.featured_image}
            onChange={(e) => {
              setFormData({...formData, featured_image: e.target.value});
              setHasChanges(true);
            }}
            className="w-full border rounded px-4 py-2"
          />
        </div>

        <div className="flex gap-4">
          <button
            type="submit"
            disabled={loading}
            className="bg-green-600 text-white px-6 py-3 rounded hover:bg-green-700 font-semibold disabled:opacity-50"
          >
            {loading ? 'Saving...' : (post ? '✓ Update Post' : '✓ Publish Post')}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="bg-red-500 text-white px-6 py-3 rounded hover:bg-red-600 font-semibold"
          >
            ✕ Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

// Users List View
const UsersListView = ({ user: currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [walletModal, setWalletModal] = useState(null);
  const [walletAmount, setWalletAmount] = useState('');
  const usersPerPage = 15;
  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    fetchUsers();
  }, [currentPage, searchQuery, sortBy, sortOrder]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/admin/users?limit=${usersPerPage}&page=${currentPage}&sort_by=${sortBy}&sort_order=${sortOrder}`;
      if (searchQuery) url += `&search=${encodeURIComponent(searchQuery)}`;
      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.data.users) {
        setUsers(response.data.users);
        setTotalUsers(response.data.total || response.data.users.length);
      } else {
        setUsers(Array.isArray(response.data) ? response.data : []);
        setTotalUsers(Array.isArray(response.data) ? response.data.length : 0);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const changeUserRole = async (userId, newRole) => {
    if (!window.confirm(`Change user role to ${newRole}?`)) return;

    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/admin/users/${userId}/role`, { role: newRole }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      alert('Role updated successfully!');
      fetchUsers();
    } catch (error) {
      alert('Error updating role');
    }
  };

  const toggleTester = async (userId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/admin/users/${userId}/tester`, { is_tester: !currentStatus }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Update locally without refetching
      setUsers(users.map(u => u.id === userId ? { ...u, is_tester: !currentStatus } : u));
    } catch (error) {
      alert('Error updating tester status: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleWalletUpdate = async (action) => {
    if (!walletModal || !walletAmount) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.patch(`${API}/admin/users/${walletModal.userId}/wallet`, {
        action: action,
        amount: parseFloat(walletAmount)
      }, { headers: { 'Authorization': `Bearer ${token}` } });
      alert(res.data.message);
      setWalletModal(null);
      setWalletAmount('');
      fetchUsers();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder(field === 'name' || field === 'email' ? 'asc' : 'desc');
    }
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setCurrentPage(1);
  };

  const formatDate = (dateStr) => {
    try {
      const d = new Date(dateStr);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      return `${dd}-${mm}-${yyyy}`;
    } catch { return dateStr; }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="ml-1 text-blue-600">{sortOrder === 'asc' ? '↑' : '↓'}</span>;
  };

  const totalPages = Math.ceil(totalUsers / usersPerPage);

  return (
    <div className="bg-white rounded-lg shadow-lg">
      {/* Header */}
      <div className="p-6 border-b bg-gradient-to-r from-purple-50 to-blue-50">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h3 className="text-2xl font-bold text-gray-800">👥 All Users</h3>
            <p className="text-sm text-gray-600 mt-1">{totalUsers} users found</p>
          </div>
          {/* Search Box */}
          <form onSubmit={handleSearch} className="flex gap-2 w-full sm:w-auto">
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search name, email, phone, city..."
              className="border border-gray-300 rounded-lg px-4 py-2 text-sm w-full sm:w-72 focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
            />
            <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 whitespace-nowrap">
              🔍 Search
            </button>
            {searchQuery && (
              <button type="button" onClick={() => { setSearchInput(''); setSearchQuery(''); setCurrentPage(1); }}
                className="bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm hover:bg-gray-300" title="Clear search">
                ✕
              </button>
            )}
          </form>
        </div>
        {searchQuery && (
          <div className="mt-2 text-sm text-blue-600">
            Showing results for: <strong>"{searchQuery}"</strong> ({totalUsers} found)
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-3 text-gray-500">Loading users...</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-100 border-b-2">
              <tr>
                <th className="text-left p-3 font-semibold text-xs text-gray-500">#</th>
                <th className="text-left p-3 font-semibold cursor-pointer hover:text-blue-600 select-none" onClick={() => handleSort('name')}>
                  Name <SortIcon field="name" />
                </th>
                <th className="text-left p-3 font-semibold cursor-pointer hover:text-blue-600 select-none" onClick={() => handleSort('email')}>
                  Email <SortIcon field="email" />
                </th>
                <th className="text-left p-3 font-semibold cursor-pointer hover:text-blue-600 select-none" onClick={() => handleSort('role')}>
                  Role <SortIcon field="role" />
                </th>
                <th className="text-center p-3 font-semibold">Premium</th>
                <th className="text-center p-3 font-semibold cursor-pointer hover:text-blue-600 select-none" onClick={() => handleSort('wallet_balance')}>
                  Balance <SortIcon field="wallet_balance" />
                </th>
                <th className="text-center p-3 font-semibold">Tester</th>
                <th className="text-left p-3 font-semibold cursor-pointer hover:text-blue-600 select-none" onClick={() => handleSort('created_at')}>
                  Joined <SortIcon field="created_at" />
                </th>
                {isAdmin && <th className="text-left p-3 font-semibold">ID</th>}
              </tr>
            </thead>
            <tbody>
              {users.map((u, idx) => (
                <tr key={u.id} className="border-b hover:bg-blue-50/30 transition-colors">
                  <td className="p-3 text-gray-400 text-xs">{(currentPage - 1) * usersPerPage + idx + 1}</td>
                  <td className="p-3 font-semibold text-gray-800">{u.name}</td>
                  <td className="p-3 text-gray-600">{u.email}</td>
                  <td className="p-3">
                    {isAdmin ? (
                      <select value={u.role} onChange={(e) => changeUserRole(u.id, e.target.value)}
                        className={`border rounded px-2 py-1 text-xs font-bold ${
                          u.role === 'admin' ? 'bg-red-50 text-red-700 border-red-200' :
                          u.role === 'moderator' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                          'bg-gray-50 text-gray-700 border-gray-200'}`}>
                        <option value="user">User</option>
                        <option value="moderator">Moderator</option>
                        <option value="admin">Admin</option>
                      </select>
                    ) : (
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        u.role === 'admin' ? 'bg-red-100 text-red-800' :
                        u.role === 'moderator' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'}`}>{u.role}</span>
                    )}
                  </td>
                  <td className="p-3 text-center">
                    {u.is_premium || u.role === 'admin' ? (
                      <span className="bg-yellow-100 text-yellow-800 border border-yellow-300 px-2 py-0.5 rounded-full text-xs font-bold">⭐ Premium</span>
                    ) : <span className="text-gray-400 text-xs">—</span>}
                  </td>
                  <td className="p-3 text-center">
                    <div className="flex items-center justify-center gap-1">
                      <span className={`font-bold text-xs ${(u.wallet_balance || 0) > 0 ? 'text-green-700' : 'text-gray-400'}`}>
                        ₹{(u.wallet_balance || 0).toFixed(0)}
                      </span>
                      {isAdmin && (
                        <button onClick={() => setWalletModal({ userId: u.id, userName: u.name, balance: u.wallet_balance || 0 })}
                          className="text-blue-500 hover:text-blue-700 ml-1" title="Manage wallet">💰</button>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    {isAdmin ? (
                      <label className="inline-flex items-center cursor-pointer" title={u.is_tester ? 'Tester: ON' : 'Tester: OFF'}>
                        <input type="checkbox" checked={!!u.is_tester} onChange={() => toggleTester(u.id, u.is_tester)} className="sr-only" />
                        <div className={`relative w-9 h-5 rounded-full transition-colors ${u.is_tester ? 'bg-green-500' : 'bg-gray-300'}`}>
                          <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${u.is_tester ? 'translate-x-4' : ''}`}></div>
                        </div>
                      </label>
                    ) : (
                      <span className={`text-xs font-bold ${u.is_tester ? 'text-green-600' : 'text-gray-400'}`}>{u.is_tester ? '✅' : '—'}</span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-gray-600">{formatDate(u.created_at)}</td>
                  {isAdmin && <td className="p-3"><span className="text-gray-400 text-xs font-mono" title={u.id}>{u.id?.substring(0, 8)}...</span></td>}
                </tr>
              ))}
              {users.length === 0 && <tr><td colSpan={9} className="text-center py-8 text-gray-500">No users found</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="p-4 border-t bg-gray-50 flex flex-col sm:flex-row justify-between items-center gap-3">
        <div className="text-sm text-gray-600">Page {currentPage} of {totalPages} • {totalUsers} total users</div>
        <div className="flex gap-2">
          <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-3 py-1.5 bg-white border rounded text-sm hover:bg-gray-50 disabled:opacity-40">«</button>
          <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1.5 bg-white border rounded text-sm hover:bg-gray-50 disabled:opacity-40">‹ Prev</button>
          <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages} className="px-3 py-1.5 bg-white border rounded text-sm hover:bg-gray-50 disabled:opacity-40">Next ›</button>
          <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage >= totalPages} className="px-3 py-1.5 bg-white border rounded text-sm hover:bg-gray-50 disabled:opacity-40">»</button>
        </div>
      </div>

      {/* Wallet Modal */}
      {walletModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setWalletModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-800 mb-1">💰 Manage Wallet</h3>
            <p className="text-sm text-gray-500 mb-4">{walletModal.userName}</p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 text-center">
              <p className="text-xs text-green-600">Current Balance</p>
              <p className="text-2xl font-black text-green-700">₹{walletModal.balance.toFixed(2)}</p>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Amount (₹)</label>
              <input type="number" value={walletAmount} onChange={(e) => setWalletAmount(e.target.value)}
                placeholder="Enter amount" className="w-full border rounded-lg px-3 py-2 text-lg font-bold" min="0" step="1" />
            </div>
            <div className="flex gap-2">
              <button onClick={() => handleWalletUpdate('add')} className="flex-1 bg-green-600 text-white py-2 rounded-lg font-bold hover:bg-green-700 text-sm">➕ Add to Wallet</button>
              <button onClick={() => handleWalletUpdate('set')} className="flex-1 bg-blue-600 text-white py-2 rounded-lg font-bold hover:bg-blue-700 text-sm">Set Balance</button>
            </div>
            <button onClick={() => setWalletModal(null)} className="w-full mt-2 py-2 text-gray-500 hover:text-gray-700 text-sm">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
};

// Components removed - Dropdown functionality has been removed from Admin Panel

export default AdminPanel;
// QA Admin View
const QAAdminView = () => {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [answeringId, setAnsweringId] = useState(null);
  const [answerText, setAnswerText] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [stats, setStats] = useState(null);

  useEffect(() => { fetchQuestions(); fetchStats(); }, [filterStatus]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/qa/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchQuestions = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/qa/admin/list`;
      if (filterStatus) url += `?status=${filterStatus}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQuestions(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const submitAnswer = async (id) => {
    if (!answerText) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/qa/admin/answer/${id}`, { answer: answerText }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnsweringId(null);
      setAnswerText('');
      fetchQuestions();
      fetchStats();
    } catch (e) {
      alert('Failed to answer question');
    }
  };

  const deleteQuestion = async (id) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/qa/admin/delete/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchQuestions();
      fetchStats();
    } catch (e) {
      alert('Failed to delete');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-bold mb-4">Q&A Management</h2>
      
      {/* Stats Bar */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <div className="bg-blue-50 p-3 rounded-lg text-center">
            <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
            <p className="text-xs text-blue-600">Total</p>
          </div>
          <div className="bg-orange-50 p-3 rounded-lg text-center">
            <p className="text-2xl font-bold text-orange-700">{stats.pending}</p>
            <p className="text-xs text-orange-600">Pending</p>
          </div>
          <div className="bg-green-50 p-3 rounded-lg text-center">
            <p className="text-2xl font-bold text-green-700">{stats.answered}</p>
            <p className="text-xs text-green-600">Answered</p>
          </div>
          <div className="bg-purple-50 p-3 rounded-lg text-center">
            <p className="text-2xl font-bold text-purple-700">{stats.from_archive}</p>
            <p className="text-xs text-purple-600">From Archive</p>
          </div>
          <div className="bg-indigo-50 p-3 rounded-lg text-center">
            <p className="text-2xl font-bold text-indigo-700">{stats.from_website}</p>
            <p className="text-xs text-indigo-600">From Website</p>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex gap-2 mb-4">
        {['', 'pending', 'answered'].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)}
            className={`px-3 py-1.5 rounded text-sm font-medium ${filterStatus === s ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
            {s === '' ? 'All' : s === 'pending' ? '⏳ Pending' : '✅ Answered'}
          </button>
        ))}
      </div>

      {loading ? <p>Loading...</p> : questions.length === 0 ? <p>No questions found.</p> : (
        <div className="space-y-4">
          {questions.slice(0, 50).map(q => {
            // Format date helper
            const formatDate = (dateStr) => {
              if (!dateStr) return null;
              try {
                const d = new Date(dateStr);
                if (isNaN(d.getTime())) return dateStr;
                return d.toLocaleDateString('en-IN', {
                  day: '2-digit', month: 'short', year: 'numeric',
                  hour: '2-digit', minute: '2-digit', hour12: true
                });
              } catch { return dateStr; }
            };
            // Time-ago helper
            const timeAgo = (dateStr) => {
              if (!dateStr) return '';
              try {
                const d = new Date(dateStr);
                if (isNaN(d.getTime())) return '';
                const now = new Date();
                const diffMs = now - d;
                const mins = Math.floor(diffMs / 60000);
                if (mins < 1) return 'Just now';
                if (mins < 60) return `${mins}m ago`;
                const hrs = Math.floor(mins / 60);
                if (hrs < 24) return `${hrs}h ago`;
                const days = Math.floor(hrs / 24);
                if (days < 30) return `${days}d ago`;
                const months = Math.floor(days / 30);
                if (months < 12) return `${months}mo ago`;
                return `${Math.floor(months / 12)}y ago`;
              } catch { return ''; }
            };

            const postedDate = formatDate(q.created_at);
            const postedAgo = timeAgo(q.created_at);
            const answeredDate = formatDate(q.answered_at);

            return (
            <div key={q.id} className={`p-4 border-2 rounded-xl ${q.status === 'pending' ? 'border-orange-300 bg-orange-50/50' : 'border-green-300 bg-green-50/50'}`}>
              {/* Top Row: Source Badge + Date + Delete */}
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  {q.source === 'gmail_archive' && <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full font-medium">📧 Archive</span>}
                  {q.source === 'website' && <span className="text-xs bg-blue-200 text-blue-700 px-2 py-0.5 rounded-full font-medium">🌐 Website</span>}
                  {q.category && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full font-medium">{q.category}</span>}
                  {q.status === 'pending' && <span className="text-xs bg-orange-200 text-orange-800 px-2 py-0.5 rounded-full font-semibold animate-pulse">⏳ Pending</span>}
                  {q.status === 'answered' && <span className="text-xs bg-green-200 text-green-800 px-2 py-0.5 rounded-full font-semibold">✅ Answered</span>}
                </div>
                <div className="flex items-center gap-2">
                  {postedDate && (
                    <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded" title={q.created_at}>
                      📅 {postedDate} {postedAgo && <span className="text-blue-600 font-medium">({postedAgo})</span>}
                    </span>
                  )}
                  <button onClick={() => deleteQuestion(q.id)} className="text-red-500 hover:text-red-700 text-sm p-1 hover:bg-red-50 rounded" title="Delete question">🗑️</button>
                </div>
              </div>

              {/* Question Title */}
              <h3 className="font-bold text-lg text-red-800 mb-1">{q.subject ? q.subject : q.question?.substring(0, 80)}</h3>
              
              {/* Question Body */}
              <p className="text-sm text-gray-700 mt-1 leading-relaxed">{q.question?.substring(0, 300)}{q.question?.length > 300 ? '...' : ''}</p>

              {/* User Details Card */}
              <div className="mt-3 p-3 bg-white/80 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-start gap-3">
                  {/* User Avatar */}
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {q.author_name ? q.author_name.charAt(0).toUpperCase() : '?'}
                  </div>
                  {/* User Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 text-sm">{q.author_name || 'Unknown'}</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-gray-600">
                      {q.author_email && <span title="Email">✉️ {q.author_email}</span>}
                      {q.author_phone && <span title="Phone">📱 {q.author_phone}</span>}
                      {q.author_location && <span title="Location">📍 {q.author_location}</span>}
                      {q.whatsapp_number && <span title="WhatsApp" className="text-green-700">💬 WA: {q.whatsapp_number}</span>}
                    </div>
                    {/* Notification Preferences */}
                    <div className="flex gap-2 mt-1.5">
                      {q.notify_whatsapp && <span className="text-xs text-green-700 bg-green-50 px-1.5 py-0.5 rounded font-medium">✅ WA Reply</span>}
                      {q.notify_comments && <span className="text-xs text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded font-medium">✅ Comment Notify</span>}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Answer Section */}
              {q.status === 'answered' ? (
                <div className="mt-3 p-3 bg-white rounded-lg border-l-4 border-green-500">
                  <div className="flex justify-between items-center mb-1">
                    <p className="text-sm font-semibold text-green-700">Answer:</p>
                    {answeredDate && <span className="text-xs text-gray-400">Answered: {answeredDate}</span>}
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800">{q.answer?.substring(0, 500)}</p>
                </div>
              ) : answeringId === q.id ? (
                <div className="mt-3">
                  <textarea className="w-full p-2 border rounded" rows="4" value={answerText} onChange={e => setAnswerText(e.target.value)} placeholder="Type your answer..."></textarea>
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => submitAnswer(q.id)} className="px-4 py-2 bg-green-600 text-white rounded text-sm">Save Answer</button>
                    <button onClick={() => setAnsweringId(null)} className="px-4 py-2 bg-gray-400 text-white rounded text-sm">Cancel</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => { setAnsweringId(q.id); setAnswerText(''); }} className="mt-3 px-4 py-2 bg-indigo-600 text-white rounded text-sm">Answer this</button>
              )}
            </div>
          );})}
          {questions.length > 50 && <p className="text-center text-gray-500 text-sm">Showing first 50 of {questions.length} questions</p>}
        </div>
      )}
    </div>
  );
};
